/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Wed Dec 27 2023
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-async-promise-executor */
/* eslint-disable @typescript-eslint/no-unused-vars */

import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import isNil from "lodash-es/isNil";
import { timer } from "rxjs";

import { Button, Column, Loader, Row, Typography, useToast } from "@/frameworks/designStuff/components";
import { POPOVER_ID, ZIndex } from "@/frameworks/core/commons";
import { usePopover, useCommandConsumer, useConfirm } from "@/frameworks/core/hooks";
import type { VoiceCallCommander, VoiceCallResponder } from "@/frameworks/core/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";
import { useVoiceCall } from "@/frameworks/core/API";

import VoiceCallUserModal from "./VoiceCallUserModal";
import * as RenderItem from "./RenderItems";
import * as S from "./emotion";

type VoiceCallProps = {
	onMemberEmpty?: () => void;
	chatSize: "full" | "mini";
	roomID: string;
};

export default function VoiceCall({ chatSize, roomID, ...rest }: VoiceCallProps) {
	const recordTimeId = useRef<number | null>(null);
	const [time, setTime] = useState(0);
	const [micState, setMicState] = useState<MediaState>("on");
	const [voiceCallJoined, setVoiceCallJoin] = useState<boolean | null>(null);
	const popover = usePopover();
	const toast = useToast();
	const { confirm } = useConfirm();
	const voiceCallAPI = useVoiceCall();

	const commander = useCommandConsumer<VoiceCallResponder, VoiceCallCommander>("VoiceCall", {
		onVoiceCallJoined(isJoined) {
			console.debug("onVoiceCallJoined isJoined", isJoined);
			if (voiceCallJoined !== isJoined) {
				setVoiceCallJoin(isJoined);
			}
		},
		onVoiceCallLoading(state) {},
		/**
		 * 보이스챗 룸 상태가 바뀔때마다 호출됨
		 */
		onVoiceCallRoomData(data) {
			if (data.members?.length <= 0) {
				rest.onMemberEmpty?.();
			}
		},
		onVoiceRecordStarted(recordStartTime) {
			console.debug("onVoiceRecordStarted recordStartTime", recordStartTime);
			const calTime = new Date().getTime() - recordStartTime;
			calTime > 0 && setTime(dayjs(new Date(calTime)).second());
			if (!recordTimeId.current) {
				recordTimeId.current = window.setInterval(() => {
					setTime((prev) => prev + 1);
				}, 1000);
			}
		},
		onVoiceRecordEnded() {
			if (recordTimeId.current) {
				window.clearInterval(recordTimeId.current);
				recordTimeId.current = null;
			}
			setTime(0);
		}
	});

	useEffect(() => {
		console.debug(`#VoiceCall# mounted ${roomID}`, { voiceCallJoined });
		timer(500).subscribe(() => {
			commander.subscribe(roomID, { voiceCallJoined, inVoiceChat: true });
		});

		return () => {
			console.debug(`#VoiceCall# unmounted ${roomID}`, { voiceCallJoined });
			commander.unsubscribe(roomID);
		};
	}, [roomID]);

	const closeRecord = () => {
		toast.show({
			type: "system",
			message: _T("보이스챗 녹음이 종료되었습니다. 저장된 파일은 파일함에서 확인 가능합니다.")
		});
		commander.command?.stop_recording(roomID);
	};

	const events = {
		handleStartVoiceCall() {
			commander.command?.start(roomID) || console.debug("#VoiceCall# commander is null");
		},
		handleExitVoiceCall() {
			if (commander.command?.get_members(roomID).length === 2) {
				confirm(_T("보이스챗을 종료하시겠습니까?"), {
					onConfirm() {
						if (commander.command?.is_recording(roomID)) {
							closeRecord();
						}
						commander.command?.stop(roomID);
					}
				});
				return;
			}
			if (commander.command?.is_recording(roomID) || false) {
				confirm(_T("음성기록 중입니다. 확인을 누르시면 음성기록이 자동으로 저장됩니다. 종료하시겠습니까?"), {
					onConfirm() {
						closeRecord();
						commander.command?.stop(roomID);
					}
				});
				return;
			}

			if (commander.command?.get_members(roomID).length === 1) {
				toast.show({
					type: "system",
					message: _T("보이스챗이 종료 되었습니다.")
				});
			}
			commander.command?.stop(roomID);
		},
		handleRecord(ev: React.MouseEvent<HTMLDivElement>) {
			const { id } = ev.currentTarget;
			if (id === "start") {
				if (commander.command?.is_recording(roomID) === false) {
					commander.command?.start_recording(roomID);
				}
			} else {
				closeRecord();
			}
		},
		handleMicToggle() {
			commander.command?.handleMic(roomID, Configure.userID, micState);
			setMicState((prev) => (prev === "on" ? "off" : prev === "off" ? "on" : "none"));
		},

		handleUserCountClicked(ev: React.MouseEvent<HTMLDivElement>) {
			popover.show({
				popoverID: POPOVER_ID.VOICECALL_USER_LIST,
				content: (
					<VoiceCallUserModal roomID={roomID} onHide={() => popover.hide(POPOVER_ID.VOICECALL_USER_LIST)} />
				),
				anchorEl: ev.currentTarget,
				direction: {
					x: "center",
					y: "bottom"
				},
				position: {
					x: 0,
					y: 2
				},
				zIndex: ZIndex.POPUP + 1
			});
		}
	};

	const handleRefreshTimeVoice = async () => {
		await voiceCallAPI.refresh.execute({ roomId: roomID });
	};

	return (
		<S.VoiceCallWrapper size={chatSize}>
			<Maybe test={isNil(voiceCallJoined) === false} fallback={<Loader />}>
				<Row gap={chatSize === "full" ? 12 : 8}>
					<RenderItem.VCIcon />
					<Row gap={8}>
						<Column gap={8}>
							<Row>
								<RenderItem.VCTitle isJoined={voiceCallJoined} />
								<RenderItem.VCInfo
									members={commander.command?.get_members(roomID) || []}
									isRecording={commander.command?.is_recording(roomID) || false}
									time={time}
									onUserCountClicked={events.handleUserCountClicked}
								/>
							</Row>
							<RenderItem.VCControlPad
								micState={micState}
								isJoined={voiceCallJoined}
								isRecording={commander.command?.is_recording(roomID) || false}
								onRecord={events.handleRecord}
								onMicToggle={events.handleMicToggle}
							/>
							{commander.command?.get_members(roomID).length === 1 && (
								<Row>
									<Typography typoSize="Body5_SB" typoColor={"warning_20_100"}>
										{_T("참여자가 없으면 1분후 자동 종료됩니다")}
									</Typography>
									<Maybe test={voiceCallJoined}>
										<Button
											css={{
												alignItems: "end",
												position: "absolute",
												bottom: "12px",
												right: "16px"
											}}
											size="mini"
											filled="primary"
											onClick={handleRefreshTimeVoice}
										>
											{_T("연장")}
										</Button>
									</Maybe>
								</Row>
							)}
						</Column>
					</Row>
				</Row>
				<RenderItem.VCStartButton
					chatSize={chatSize}
					isJoined={Boolean(voiceCallJoined)}
					isLoading={voiceCallJoined}
					onStart={events.handleStartVoiceCall}
					onExit={events.handleExitVoiceCall}
					numberOfMembers={commander.command?.get_members(roomID).length}
				/>
			</Maybe>
		</S.VoiceCallWrapper>
	);
}
